import { useOptionalMeasurements } from '../../context/MeasurementContext'
import { convertMeasurement, measurementUnit } from '../../lib/measurement'
import { readCsv, parseMeasurementCsv, csvCell } from '../../lib/measurementExchange'
import { measurementItem } from '../../lib/measurementView'
import { Measure } from '../Measurement'
import React, { useState } from 'react'
import Dialog from '../ui/Dialog'
import { Item3D } from '../../types/optimization'
import { v4 as uuidv4 } from 'uuid'
import {
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    DocumentTextIcon,
    CheckCircleIcon,
    ExclamationCircleIcon
} from '@heroicons/react/24/outline'

interface CargoCsvDialogProps {
    isOpen: boolean
    onClose: () => void
    onImportItems: (items: Item3D[], mode: 'append' | 'replace') => void
    currentItems: Item3D[]
}

const PREMIUM_COLORS = [
    '#2563eb', '#059669', '#7c3aed', '#d97706', '#db2777',
    '#0891b2', '#ea580c', '#0d9488', '#4f46e5', '#9333ea'
]

export default function CargoCsvDialog({
    isOpen,
    onClose,
    onImportItems,
    currentItems
}: CargoCsvDialogProps) {
    const system = useOptionalMeasurements()?.system ?? 'metric'
    const dimensionUnit = measurementUnit(system, 'cargoLength'), massUnit = measurementUnit(system, 'mass')
    const [activeTab, setActiveTab] = useState<'export' | 'import'>('import')
    const [csvText, setCsvText] = useState('')
    const [importMode, setImportMode] = useState<'append' | 'replace'>('append')
    const [parsedPreview, setParsedPreview] = useState<Item3D[] | null>(null)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)

    const handleParseCsv = (text: string) => {
        setCsvText(text)
        setErrorMsg(null)
        setParsedPreview(null)

        if (!text.trim()) return

        try {
            const items = parseMeasurementCsv(text, 'cargo').map((item, index) => ({
                ...item, id: item.sku || uuidv4(), color: PREMIUM_COLORS[(index + currentItems.length) % PREMIUM_COLORS.length],
                _dndId: uuidv4(),
                rotationOptions: {
                    allowRotationX: false, allowRotationY: false, allowRotationZ: true,
                    rotationStrategy: 'DEFAULT_FLAT_ROTATIONS',
                },
            }))
            setParsedPreview(items)
        } catch (err: any) {
            setErrorMsg(`Erreur de lecture : ${err.message || err}`)
        }
    }

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        const reader = new FileReader()
        reader.onload = (event) => {
            const content = event.target?.result as string
            if (content) {
                handleParseCsv(content)
            }
        }
        reader.readAsText(file)
    }

    const handleConfirmImport = () => {
        if (parsedPreview && parsedPreview.length > 0) {
            onImportItems(parsedPreview, importMode)
            onClose()
            setCsvText('')
            setParsedPreview(null)
        }
    }

    const generateExportCsv = (): string => {
        const header = `SKU;Description;Longueur_${dimensionUnit};Largeur_${dimensionUnit};Hauteur_${dimensionUnit};Poids_${massUnit};Quantite;Groupe;Empilable\n`
        const rows = currentItems.map(source => {
            const item = measurementItem(source)
            return [
                item.id || '',
                item.description || '',
                convertMeasurement(item.length ?? 0, 'mm', dimensionUnit),
                convertMeasurement(item.width ?? 0, 'mm', dimensionUnit),
                convertMeasurement(item.height ?? 0, 'mm', dimensionUnit),
                convertMeasurement(item.weight ?? 0, 'kg', massUnit),
                item.quantity || 1,
                item.keep_together_group || '',
                item.stackable ? 'oui' : 'non'
            ].map(csvCell).join(';')
        }).join('\n')

        return header + rows
    }

    const handleDownloadCsv = () => {
        const csvContent = generateExportCsv()
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `cargaison_${new Date().toISOString().slice(0, 10)}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const handleDownloadTemplate = () => {
        const template = 'SKU;Description;Longueur_mm;Largeur_mm;Hauteur_mm;Poids_kg;Quantite;Groupe;Empilable\n' +
            'PAL-EUR-01;Palette Europe Standard;1200;800;144;25;10;C-100;oui\n' +
            'PAL-IND-02;Palette Industrielle ISO;1200;1000;144;30;5;C-100;oui\n' +
            'CARTON-A;Carton Matériel High-Tech;600;400;400;12.5;20;;oui\n'

        const rows = readCsv(template)
        rows[0] = rows[0].map(h => h.replace('_mm', '_' + dimensionUnit).replace('_kg', '_' + massUnit))
        for (const row of rows.slice(1)) {
            for (const i of [2, 3, 4]) row[i] = String(convertMeasurement(Number(row[i]), 'mm', dimensionUnit))
            row[5] = String(convertMeasurement(Number(row[5]), 'kg', massUnit))
        }
        const convertedTemplate = rows.map(row => row.map(csvCell).join(';')).join('\n')
        const blob = new Blob([convertedTemplate], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', 'modele_import_cargaison.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            title="Import / Export CSV Cargaison"
            size="lg"
        >
            <div className="space-y-5">

                {/* Tab Selection */}
                <div className="flex border-b border-slate-200 dark:border-slate-700">
                    <button
                        type="button"
                        onClick={() => setActiveTab('import')}
                        className={`px-4 py-2.5 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'import'
                                ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                            }`}
                    >
                        <ArrowUpTrayIcon className="w-4 h-4" />
                        Importer des articles
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('export')}
                        className={`px-4 py-2.5 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'export'
                                ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                            }`}
                    >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        Exporter la cargaison
                    </button>
                </div>

                {/* IMPORT TAB */}
                {activeTab === 'import' && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                            <div className="text-xs text-slate-600 dark:text-slate-300">
                                <span className="font-semibold">Format supporté :</span> CSV / Texte (délimiteur point-virgule ou virgule)
                                <br />
                                <span className="font-mono text-[11px] text-slate-500">Unités explicites dans les colonnes : mm, cm, m, in, ft / kg, lb. Sans unité : mm/kg (ancien format).</span>
                            </div>
                            <button
                                type="button"
                                onClick={handleDownloadTemplate}
                                className="px-3 py-1.5 text-xs font-semibold text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/40 border border-orange-200 dark:border-orange-800 rounded-lg transition-colors flex items-center gap-1 flex-shrink-0"
                            >
                                <DocumentTextIcon className="w-4 h-4" />
                                Modèle CSV
                            </button>
                        </div>

                        {/* File Upload Zone */}
                        <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-orange-400 rounded-xl p-4 text-center transition-colors">
                            <input
                                type="file"
                                accept=".csv,.txt"
                                onChange={handleFileUpload}
                                className="hidden"
                                id="csv-file-upload"
                            />
                            <label htmlFor="csv-file-upload" className="cursor-pointer block">
                                <ArrowUpTrayIcon className="w-8 h-8 mx-auto text-orange-500 mb-1" />
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                    Parcourir un fichier CSV ou glisser-déposer
                                </span>
                                <p className="text-xs text-slate-400 mt-0.5">ou collez le texte CSV ci-dessous</p>
                            </label>
                        </div>

                        {/* Textarea */}
                        <div>
                            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                Contenu CSV brut :
                            </label>
                            <textarea
                                rows={5}
                                value={csvText}
                                onChange={(e) => handleParseCsv(e.target.value)}
                                placeholder={`SKU;Description;Longueur_mm;Largeur_mm;Hauteur_mm;Poids_kg;Quantite;Groupe\nPAL-01;Palette Boissons;1200;800;1440;650;4;GRP-A`}
                                className="w-full text-xs font-mono p-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                            />
                        </div>

                        {/* Parsing error */}
                        {errorMsg && (
                            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                                <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
                                <span className="whitespace-pre-line">{errorMsg}</span>
                            </div>
                        )}

                        {/* Parsed Preview Table */}
                        {parsedPreview && parsedPreview.length > 0 && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                        <CheckCircleIcon className="w-4 h-4" />
                                        {parsedPreview.length} article(s) détecté(s) avec succès
                                    </span>
                                    <div className="flex items-center gap-3 text-xs">
                                        <label className="flex items-center gap-1.5 cursor-pointer text-slate-600 dark:text-slate-300">
                                            <input
                                                type="radio"
                                                name="importMode"
                                                checked={importMode === 'append'}
                                                onChange={() => setImportMode('append')}
                                                className="text-orange-500 focus:ring-orange-500"
                                            />
                                            Ajouter à la cargaison existante
                                        </label>
                                        <label className="flex items-center gap-1.5 cursor-pointer text-slate-600 dark:text-slate-300">
                                            <input
                                                type="radio"
                                                name="importMode"
                                                checked={importMode === 'replace'}
                                                onChange={() => setImportMode('replace')}
                                                className="text-orange-500 focus:ring-orange-500"
                                            />
                                            Remplacer la cargaison
                                        </label>
                                    </div>
                                </div>

                                <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl">
                                    <table className="w-full text-xs text-left">
                                        <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold sticky top-0">
                                            <tr>
                                                <th className="p-2">SKU</th>
                                                <th className="p-2">Description</th>
                                                <th className="p-2 text-right">L</th>
                                                <th className="p-2 text-right">l</th>
                                                <th className="p-2 text-right">H</th>
                                                <th className="p-2 text-right">Poids</th>
                                                <th className="p-2 text-right">Qté</th>
                                                <th className="p-2">Groupe</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {parsedPreview.map((item, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                                    <td className="p-2 font-mono font-semibold">{item.id}</td>
                                                    <td className="p-2 text-slate-600 dark:text-slate-400">{item.description || '—'}</td>
                                                    <td className="p-2 text-right"><Measure value={item.length} from="mm" quantity="cargoLength" /></td>
                                                    <td className="p-2 text-right"><Measure value={item.width} from="mm" quantity="cargoLength" /></td>
                                                    <td className="p-2 text-right"><Measure value={item.height} from="mm" quantity="cargoLength" /></td>
                                                    <td className="p-2 text-right"><Measure value={item.weight} from="kg" quantity="mass" /></td>
                                                    <td className="p-2 text-right font-semibold">{item.quantity}</td>
                                                    <td className="p-2">{item.keep_together_group || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-end gap-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                disabled={!parsedPreview || parsedPreview.length === 0}
                                onClick={handleConfirmImport}
                                className="px-5 py-2 text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm"
                            >
                                Importer {parsedPreview ? `(${parsedPreview.length} articles)` : ''}
                            </button>
                        </div>
                    </div>
                )}

                {/* EXPORT TAB */}
                {activeTab === 'export' && (
                    <div className="space-y-4">
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                            Exporte les <span className="font-bold">{currentItems.length} articles</span> de la cargaison actuelle au format CSV compatible Excel / TMS.
                        </p>

                        <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2">
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Aperçu du CSV d&apos;export :</span>
                            <pre className="text-[11px] font-mono p-3 bg-white dark:bg-slate-900 rounded-lg max-h-40 overflow-auto text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800">
                                {generateExportCsv()}
                            </pre>
                        </div>

                        <div className="flex justify-end gap-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                            >
                                Fermer
                            </button>
                            <button
                                type="button"
                                disabled={currentItems.length === 0}
                                onClick={handleDownloadCsv}
                                className="px-5 py-2 text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm flex items-center gap-1.5"
                            >
                                <ArrowDownTrayIcon className="w-4 h-4" />
                                Télécharger le fichier CSV
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </Dialog>
    )
}
